import {
    flush,
    getUserId,
    identify,
    Identify,
    init,
    reset,
    setTransport,
    setUserId,
    track,
} from '@amplitude/analytics-browser';
import type { BrowserOptions } from '@amplitude/analytics-browser/lib/esm/types';
import {
    DEFAULT_ACTION_CLICK_ALLOWLIST,
    DEFAULT_CSS_SELECTOR_ALLOWLIST,
    LogLevel,
} from '@amplitude/analytics-browser/lib/esm/types';
import type {
    AnalyticsTransport,
    AnalyticsUserProperties,
    TimestampedAnalyticsEvent,
} from '@thinkalpha/ui-ioc/core/types/AnalyticsService';
import type { ConfigService } from '@thinkalpha/ui-ioc/core/types/ConfigService';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { UserDetails } from '@thinkalpha/ui-types/types/user';
import { inject, injectable } from 'inversify';
import { ANALYTICS_SCOPE_ATTRIBUTE, AnalyticsScope } from 'src/lib/analytics/scopes';

const TRACKED_ELEMENT_SELECTORS = [
    ...DEFAULT_CSS_SELECTOR_ALLOWLIST,
    // zag.js controls that are not native form elements
    '[role="button"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="tab"]',
];

/** Restricts selectors to elements inside one of the declared analytics scopes */
const scoped = (selectors: readonly string[]): string[] =>
    Object.values(AnalyticsScope).flatMap((scope) =>
        selectors.map((selector) => `[${ANALYTICS_SCOPE_ATTRIBUTE}="${scope}"] ${selector}`),
    );

export const AMPLITUDE_INIT_OPTIONS = {
    autocapture: {
        // Sessions and attribution (UTM, referrer) feed the signup funnel's entry step
        attribution: true,
        sessions: true,
        // Our page_viewed event covers page views without the query string. Amplitude's sends the
        // full URL, which on /oauth2/callback includes the authorization code and state.
        pageViews: false,
        elementInteractions: {
            cssSelectorAllowlist: scoped(TRACKED_ELEMENT_SELECTORS),
            // Clicks on divs, spans and headings that change the page are tracked through this
            // separate list, which defaults to every such element on the page
            actionClickAllowlist: scoped(DEFAULT_ACTION_CLICK_ALLOWLIST),
        },
        formInteractions: false,
        fileDownloads: false,
    },
    // Autocapture settings live in code only; remote config from the Amplitude UI would override them
    fetchRemoteConfig: false,
    logLevel: LogLevel.None,
} satisfies BrowserOptions;

@injectable()
export class AmplitudeWebTransport implements AnalyticsTransport, Disposable {
    #initialized = false;
    readonly #logger: Logger;
    readonly #configService: ConfigService;
    /**
     * Identity changes run in order once `init()` has restored the persisted identity. Clearing
     * depends on that restored state, and a clear deferred behind a newer `setUserId` would undo it.
     */
    #identityChanges: Promise<void> = Promise.resolve();
    readonly #pageListeners = new AbortController();

    constructor(@inject('Logger') baseLogger: Logger, @inject('ConfigService') configService: ConfigService) {
        this.#logger = baseLogger.getSubLogger({ name: 'amplitude-transport' });
        this.#configService = configService;
        this.#initialize();
    }

    #initialize(): void {
        const amplitudeConfig = this.#configService.amplitudeConfig;
        if (!amplitudeConfig) {
            this.#logger.info(`Amplitude disabled in ${this.#configService.environmentName}`);
            return;
        }

        try {
            this.#identityChanges = init(amplitudeConfig.apiKey, {
                ...AMPLITUDE_INIT_OPTIONS,
                appVersion: amplitudeConfig.appVersion,
            }).promise.then(
                () => this.#logger.info('Amplitude initialized successfully'),
                (error: unknown) =>
                    this.#logger.error('Failed to initialize Amplitude', {
                        error: error instanceof Error ? error : new Error(String(error)),
                    }),
            );

            this.#setNoUserIdentify();
            this.#flushWhenLeavingPage();

            this.#initialized = true;
        } catch (error) {
            this.#logger.error('Failed to initialize Amplitude', {
                error: error instanceof Error ? error : new Error(String(error)),
            });
        }
    }

    /**
     * The SDK sends on a timer and does not flush when the page goes away, so events from the last
     * moments before a tab closes or a full-page redirect (such as to Keycloak) would wait in local
     * storage until the next visit. A beacon request survives the page unloading.
     */
    #flushWhenLeavingPage(): void {
        if (typeof window === 'undefined') return;

        const { signal } = this.#pageListeners;
        window.addEventListener(
            'pagehide',
            () => {
                setTransport('beacon');
                flush();
            },
            { signal },
        );
        // A page restored from the back/forward cache keeps running; fetch retries failed sends
        window.addEventListener(
            'pageshow',
            (event) => {
                if (event.persisted) setTransport('fetch');
            },
            { signal },
        );
    }

    async sendEvents(events: TimestampedAnalyticsEvent[]): Promise<void> {
        if (!this.#initialized) return;

        await Promise.all(
            events.map(async (event) => {
                try {
                    const { type, timestamp, ...properties } = event;
                    // The event time is an option, not a property; as a property Amplitude stamps the send time
                    const result = await track(type, properties, { time: timestamp.getTime() }).promise;

                    if (result.code !== 200) {
                        throw new Error(`Amplitude track failed: ${result.message}`);
                    }

                    if (TRACE) {
                        this.#logger.trace(`Sent event to Amplitude: ${type}`, { properties });
                    }
                } catch (error) {
                    this.#logger.error(`Failed to send event to Amplitude: ${event.type}`, {
                        error: error instanceof Error ? error : new Error(String(error)),
                        event,
                    });
                }
            }),
        );
    }

    #setNoUserIdentify(): void {
        const identifyObj = new Identify();

        // Set the env at least
        identifyObj.set('env', this.#configService.environmentName);

        identify(identifyObj);
    }

    #enqueueIdentityChange(description: string, change: () => void): void {
        this.#identityChanges = this.#identityChanges.then(() => {
            try {
                change();
            } catch (error) {
                this.#logger.error(`Failed to ${description} in Amplitude`, {
                    error: error instanceof Error ? error : new Error(String(error)),
                });
            }
        });
    }

    setUser(user: UserDetails, properties?: AnalyticsUserProperties): void {
        if (!this.#initialized) return;

        this.#enqueueIdentityChange('set user', () => {
            setUserId(user.id);

            const identifyObj = new Identify();
            identifyObj.set('email', user.email);
            identifyObj.set('firstName', user.firstName);
            identifyObj.set('lastName', user.lastName);
            identifyObj.set('name', `${user.firstName} ${user.lastName}`);
            identifyObj.set('isSuperuser', user.isSuperuser);
            identifyObj.set('isDeveloper', user.isDeveloper);
            identifyObj.set('isTestUser', user.isTestUser ?? false);
            identifyObj.set('env', this.#configService.environmentName);

            if (properties) {
                identifyObj.set('isSubscriptionUser', properties.isSubscriptionUser);
                identifyObj.set('isFirmUser', properties.isFirmUser);
                identifyObj.set('subscriptionStatus', properties.subscriptionStatus);
            }

            identify(identifyObj);

            if (TRACE) {
                this.#logger.debug(`Set Amplitude user: ${user.id}`);
            }
        });
    }

    /**
     * Resets only when the SDK holds a user ID, set on this page or restored from an earlier
     * visit's cookie. Resetting also issues a new device ID, so an anonymous visitor keeps theirs
     * across the login redirect.
     */
    clearUser(): void {
        if (!this.#initialized) return;

        this.#enqueueIdentityChange('clear user', () => {
            const userId = getUserId();
            if (userId === undefined) return;

            reset();
            this.#setNoUserIdentify();

            if (TRACE) {
                this.#logger.debug(`Cleared Amplitude user ${userId}`);
            }
        });
    }

    [Symbol.dispose](): void {
        this.#pageListeners.abort();
    }
}
